import React from 'react'
import type { TranslateFn, LotStoryLibraryCompareActions, LotStoryLibraryCompareState } from './lotStoryLibraryTypes'
import type { LotStoryLibraryPresentation } from './lotStoryPresentation'
import LotStoryLibraryCompareDiffTable from './LotStoryLibraryCompareDiffTable'
import LotStoryLibraryCompareSnapshotCard from './LotStoryLibraryCompareSnapshotCard'

type LotStoryLibraryComparePanelProps = {
  t: TranslateFn
  compareState: LotStoryLibraryCompareState
  compareActions: LotStoryLibraryCompareActions
  presentation: LotStoryLibraryPresentation
}

export default function LotStoryLibraryComparePanel({
  t,
  compareState,
  compareActions,
  presentation
}: Readonly<LotStoryLibraryComparePanelProps>) {
  const {
    artifact,
    selectedStoryPreviewVersionId,
    compareBaseStoryVersion,
    compareTargetStoryVersion
  } = compareState
  const {
    onClearCompare,
    onOpenArtifactVersion
  } = compareActions

  return (
    <div style={{ border: '1px solid #bfdbfe', borderRadius: 10, backgroundColor: '#f8fbff', padding: 12, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <strong style={{ display: 'block', fontSize: 13, color: '#1d4ed8' }}>{t('lots.story.library.compare.title')}</strong>
          <div style={{ marginTop: 2, fontSize: 12, color: '#475569' }}>
            {t('lots.story.library.compare.subtitle', {
              left: compareBaseStoryVersion.versionNumber,
              right: compareTargetStoryVersion.versionNumber
            })}
          </div>
        </div>
        <button
          type="button"
          onClick={onClearCompare}
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid #cbd5e1',
            backgroundColor: '#ffffff',
            color: '#0f172a',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600
          }}
        >
          {t('lots.story.library.compare.clear')}
        </button>
      </div>

      <LotStoryLibraryCompareDiffTable
        t={t}
        compareBaseStoryVersion={compareBaseStoryVersion}
        compareTargetStoryVersion={compareTargetStoryVersion}
        presentation={presentation}
      />

      <div style={{ display: 'grid', gap: 10 }}>
        <div>
          <strong style={{ display: 'block', fontSize: 13, color: '#1d4ed8' }}>{t('lots.story.library.compare.snapshotTitle')}</strong>
          <div style={{ marginTop: 2, fontSize: 12, color: '#475569' }}>{t('lots.story.library.compare.snapshotSubtitle')}</div>
        </div>

        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          {[
            {
              key: 'base',
              heading: t('lots.story.library.compare.snapshotBase', { version: compareBaseStoryVersion.versionNumber }),
              version: compareBaseStoryVersion,
              accentBorder: '#fcd34d',
              accentBackground: '#fffbeb',
              accentText: '#92400e'
            },
            {
              key: 'target',
              heading: t('lots.story.library.compare.snapshotTarget', { version: compareTargetStoryVersion.versionNumber }),
              version: compareTargetStoryVersion,
              accentBorder: '#93c5fd',
              accentBackground: '#f8fbff',
              accentText: '#1d4ed8'
            }
          ].map(({ key, heading, version, accentBorder, accentBackground, accentText }) => (
            <LotStoryLibraryCompareSnapshotCard
              key={key}
              t={t}
              artifact={artifact}
              heading={heading}
              version={version}
              selectedStoryPreviewVersionId={selectedStoryPreviewVersionId}
              accentBorder={accentBorder}
              accentBackground={accentBackground}
              accentText={accentText}
              onOpenArtifactVersion={onOpenArtifactVersion}
              presentation={presentation}
            />
          ))}
        </div>
      </div>
    </div>
  )
}